import type { ProgressStatusDto } from '@/queries/progress-statuses/progress-statuses.schema';
import { ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { GmStatusImage } from '@/components/entities/gamification/GmStatusImage';
import { GmStatusBlockSkeleton } from '@/components/entities/gamification/skeletons/GmStatusBlockSkeleton';
import { getStatusImagePath } from '@/components/features/gamification/model/utils/getProgressStatusAssetMedia';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';

interface GmStatusBlockProps {
  loading?: boolean;
  statusRevealProgress: number;
  currentStatus: ProgressStatusDto | null;
}

export const GmStatusBlock = ({ loading, currentStatus, statusRevealProgress }: GmStatusBlockProps) => {
  const t = useTranslations('gamification.widget-bar');
  const statusTitle = currentStatus ? t('your-status', { status: currentStatus.name }) : '';

  const statusPicture = currentStatus ? getStatusImagePath(currentStatus) : null;

  const handleStopSliderPointerEvent = (event: React.PointerEvent) => {
    event.stopPropagation();
  };

  if (loading) {
    return <GmStatusBlockSkeleton />;
  }

  return (

    <div
      className="flex items-center gap-2 px-4 pb-4 transition-[opacity,transform] duration-200"
      style={{
        opacity: statusRevealProgress,
        transform: `translate3d(0, ${(1 - statusRevealProgress) * -8}px, 0)`,
      }}
    >

      <div className="relative size-11 shrink-0 overflow-visible">
        {
          statusPicture && (
            <GmStatusImage
              draggable={false}
              src={statusPicture}
              alt={currentStatus?.name || 'Status icon'}
              className="pointer-events-none size-11 select-none"
            />

          )
        }
      </div>
      <p className="min-w-0 flex-1 pt-1 text-lg font-medium tracking-normal leading-5.5">{statusTitle}</p>
      <Link
        href={AppRoutes.gamificationAllStages}
        onPointerDown={handleStopSliderPointerEvent}
        onPointerMove={handleStopSliderPointerEvent}
        onPointerUp={handleStopSliderPointerEvent}
        onPointerCancel={handleStopSliderPointerEvent}
        className="flex shrink-0 h-7.5 touch-manipulation items-center gap-1 text-xs font-medium leading-4 text-c300 group"
      >
        <span className="transition-colors duration-300 group-hover:text-c100">{t('all-statuses')}</span>
        <ChevronRight className="size-4 transition-colors duration-300 group-hover:text-c100" aria-hidden />
      </Link>
    </div>
  );
};
